import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Divider,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { ShoppingList } from "../lib/types";
import { pageShareAPI, PageShareInfo } from "../services/api/api";
import { SearchFilterBar } from "../components/common";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0f0f0f",
      paper: "#1a1a1a",
    },
  },
});

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

const formatDate = (value?: Date): string => {
  if (!value || !(value instanceof Date)) return "—";
  return value.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const SharedShoppingPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareInfo, setShareInfo] = useState<PageShareInfo | null>(null);
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLive, setIsLive] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOption, setSortOption] = useState("title_asc");

  const loadShareInfo = useCallback(async () => {
    if (!shareId) {
      setError("Invalid share link");
      setLoading(false);
      return;
    }
    try {
      console.debug("[SharedShopping] Loading share info:", shareId);
      const info = await pageShareAPI.getInfo(shareId);
      if (info.pageType !== "shopping") {
        setError("Invalid share link");
        return;
      }
      setShareInfo(info);
      console.debug("[SharedShopping] Share info loaded:", info.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load share");
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  const validateShare = useCallback(
    async (passwordValue?: string) => {
      if (!shareId) return;
      try {
        setValidating(true);
        console.debug("[SharedShopping] Validating share:", shareId);
        const result = await pageShareAPI.validate(shareId, passwordValue);
        setLists(result.shoppingLists || []);
        setIsLive(result.isLive);
        setIsAuthenticated(true);
        console.debug("[SharedShopping] Share validated:", {
          lists: result.shoppingLists?.length || 0,
          isLive: result.isLive,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Validation failed";
        setError(message);
        console.error("[SharedShopping] Validation failed:", message);
      } finally {
        setValidating(false);
      }
    },
    [shareId]
  );

  useEffect(() => {
    void loadShareInfo();
  }, [loadShareInfo]);

  useEffect(() => {
    if (!shareInfo) return;
    if (shareInfo.requiresPassword) return;
    void validateShare();
  }, [shareInfo, validateShare]);

  const handleSubmitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password.trim()) return;
    await validateShare(password.trim());
  };

  const handleSearchApply = useCallback(() => {
    setAppliedSearchQuery(searchQuery);
    console.debug("[SharedShopping] Search applied:", searchQuery);
  }, [searchQuery]);

  const filteredLists = useMemo(() => {
    const query = appliedSearchQuery.trim().toLowerCase();
    const matchesQuery = (list: ShoppingList) => {
      if (!query) return true;
      const listMatch = list.title?.toLowerCase().includes(query);
      const itemMatch = list.items.some((item) => {
        return (
          item.title?.toLowerCase().includes(query) ||
          item.vendor?.toLowerCase().includes(query)
        );
      });
      return listMatch || itemMatch;
    };

    const matchesStatus = (list: ShoppingList) => {
      if (statusFilter === "all") return true;
      return list.status === statusFilter;
    };

    return lists.filter((list) => matchesQuery(list) && matchesStatus(list));
  }, [appliedSearchQuery, lists, statusFilter]);

  const sortedLists = useMemo(() => {
    const next = [...filteredLists];
    const getListTotal = (list: ShoppingList) =>
      list.items.reduce((sum, item) => sum + item.price, 0);
    const compare = (a: ShoppingList, b: ShoppingList) => {
      switch (sortOption) {
        case "title_desc":
          return b.title.localeCompare(a.title);
        case "items_desc":
          return b.items.length - a.items.length;
        case "items_asc":
          return a.items.length - b.items.length;
        case "total_desc":
          return getListTotal(b) - getListTotal(a);
        case "total_asc":
          return getListTotal(a) - getListTotal(b);
        case "due_asc":
          return (a.dueDate?.getTime() || 0) - (b.dueDate?.getTime() || 0);
        case "due_desc":
          return (b.dueDate?.getTime() || 0) - (a.dueDate?.getTime() || 0);
        case "title_asc":
        default:
          return a.title.localeCompare(b.title);
      }
    };
    return next.sort(compare);
  }, [filteredLists, sortOption]);

  const listTotals = useMemo(() => {
    const totalLists = sortedLists.length;
    const totalItems = sortedLists.reduce(
      (sum, list) => sum + list.items.length,
      0
    );
    const totalCost = sortedLists.reduce(
      (sum, list) =>
        sum + list.items.reduce((itemSum, item) => itemSum + item.price, 0),
      0
    );
    return { totalLists, totalItems, totalCost };
  }, [sortedLists]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0f0f0f", p: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" sx={{ color: "white", mb: 1 }}>
              Shared Shopping List
            </Typography>
            {shareInfo?.expiresAt && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)" }}
              >
                This link expires on{" "}
                {new Date(shareInfo.expiresAt).toLocaleDateString("en-US", {
                  timeZone: "UTC",
                })}
              </Typography>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error && !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {error}
              </Alert>
            </Box>
          ) : shareInfo?.requiresPassword && !isAuthenticated ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <Card sx={{ maxWidth: 420, width: "100%" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <LockIcon
                      sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                      Password Protected
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      Enter the password to view the shared shopping lists
                    </Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmitPassword}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{ mb: 2 }}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={validating || !password}
                    >
                      {validating ? (
                        <CircularProgress size={24} />
                      ) : (
                        "View Lists"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                  mb: 4,
                }}
              >
                <Card sx={{ borderTop: "4px solid #38b2ac" }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Lists
                    </Typography>
                    <Typography variant="h5" sx={{ color: "#38b2ac" }}>
                      {listTotals.totalLists}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ borderTop: "4px solid #4299e1" }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Items
                    </Typography>
                    <Typography variant="h5" sx={{ color: "#4299e1" }}>
                      {listTotals.totalItems}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ borderTop: "4px solid #f6ad55" }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Estimated Total
                    </Typography>
                    <Typography variant="h5" sx={{ color: "#f6ad55" }}>
                      {formatCurrency(listTotals.totalCost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {!isLive && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  This is a snapshot view. Updates are not live.
                </Alert>
              )}

              <SearchFilterBar
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                onSearchApply={handleSearchApply}
                searchPlaceholder="Search lists or items"
                filters={[
                  {
                    key: "status",
                    label: "Status",
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: [
                      { value: "all", label: "All Statuses" },
                      { value: "active", label: "Active" },
                      { value: "archived", label: "Archived" },
                    ],
                    minWidth: 160,
                  },
                  {
                    key: "sort",
                    label: "Sort",
                    value: sortOption,
                    onChange: setSortOption,
                    options: [
                      { value: "title_asc", label: "Title (A → Z)" },
                      { value: "title_desc", label: "Title (Z → A)" },
                      { value: "items_desc", label: "Items (High → Low)" },
                      { value: "items_asc", label: "Items (Low → High)" },
                      { value: "total_desc", label: "Total (High → Low)" },
                      { value: "total_asc", label: "Total (Low → High)" },
                      { value: "due_asc", label: "Due Date (Soonest)" },
                      { value: "due_desc", label: "Due Date (Latest)" },
                    ],
                    minWidth: 190,
                  },
                ]}
              />

              {sortedLists.length === 0 ? (
                <Alert severity="info">No shopping lists available.</Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {sortedLists.map((list) => {
                    const listTotal = list.items.reduce(
                      (sum, item) => sum + item.price,
                      0
                    );
                    return (
                      <Card key={list.id}>
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6">{list.title}</Typography>
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                            >
                              {formatCurrency(listTotal)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Status: {list.status} · Due:{" "}
                            {formatDate(list.dueDate)}
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          {list.items.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No items yet.
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {list.items.map((item) => (
                                <Box
                                  key={item.id}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    backgroundColor: "rgba(255,255,255,0.04)",
                                    borderRadius: 1,
                                    px: 2,
                                    py: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body1">
                                      {item.title}
                                    </Typography>
                                    {item.vendor && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {item.vendor}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography variant="body2">
                                    {formatCurrency(item.price)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SharedShoppingPage;
